import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { Helmet } from 'react-helmet-async';
import { Button, Result, Spin } from 'antd';
import { useUser } from '../../hooks/useUser';
import {
  useDecideMcpAuthorizationMutation,
  useGetMcpAuthorizationRequestQuery,
} from './apiMcpAuthorize';
import { navigateExternal } from './externalNavigation';

/** HTTP status of a failed call; a network-level failure carries no `response` at all. */
const statusOf = (error: unknown): number | undefined => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- narrowed by the `in` check above
    const { response } = error as { response?: { status?: number } };
    return response?.status;
  }

  return undefined;
};

const decisionErrorMessage = (error: unknown): string => {
  const status = statusOf(error);
  if (status === 409) {
    return 'This request was already decided. Go back to your MCP client and start the login again.';
  }
  if (status === 404) {
    return 'This request is unknown or has expired. Start the login again from your MCP client.';
  }
  if (status === 403) {
    return 'Your account is not allowed to use the Ydistri MCP.';
  }
  return 'The decision could not be saved. Try again, or start the login again from your MCP client.';
};

/**
 * The human step of an MCP client's login (phase 0.4.4): the API parked the client's authorization
 * request and sent the browser here; the user, already signed in (PrivateRoute), approves or denies,
 * and the browser continues to the API, which issues the code to the client's loopback callback.
 * Outside ProjectRoute on purpose - there is no project here - hence plain useParams rather than
 * useStableParams, which lives under the project layout.
 */
const McpAuthorize: React.FC = () => {
  const { requestId = '' } = useParams<{ requestId: string }>();
  const user = useUser();
  const { data, isLoading, error } = useGetMcpAuthorizationRequestQuery(requestId, {
    skip: requestId === '',
  });
  const [decide, { isLoading: deciding }] = useDecideMcpAuthorizationMutation();
  const [decisionError, setDecisionError] = useState<string>();

  const onDecision = useCallback(
    (approve: boolean) => {
      setDecisionError(undefined);
      decide({ requestId, approve })
        .unwrap()
        .then(result => {
          navigateExternal(result.continueUrl);
        })
        .catch((e: unknown) => {
          setDecisionError(decisionErrorMessage(e));
        });
    },
    [decide, requestId],
  );

  const onApprove = useCallback(() => onDecision(true), [onDecision]);
  const onDeny = useCallback(() => onDecision(false), [onDecision]);

  const actions = useMemo(
    () => [
      <Button
        key="approve"
        type="primary"
        onClick={onApprove}
        loading={deciding}
        disabled={deciding}
      >
        Approve
      </Button>,
      <Button key="deny" onClick={onDeny} disabled={deciding}>
        Deny
      </Button>,
    ],
    [onApprove, onDeny, deciding],
  );

  if (isLoading) {
    return <Spin />;
  }

  if (!data || error) {
    // A logged-in non-superadmin reaches this route (PrivateRoute has no role here) and gets 403
    // from the gate; everything else is an unknown or expired request.
    const forbidden = statusOf(error) === 403;
    return (
      <>
        <Helmet title="MCP access" />
        <Result
          status={forbidden ? '403' : '404'}
          title={forbidden ? 'Not allowed' : 'Unknown or expired request'}
          subTitle={
            forbidden
              ? 'Your account is not allowed to use the Ydistri MCP.'
              : 'Start the login again from your MCP client; the request it opened is no longer waiting here.'
          }
        />
      </>
    );
  }

  const asUser = user?.fullName ?? user?.email ?? 'you';
  const expires = new Date(data.expiresAt).toLocaleTimeString();

  return (
    <>
      <Helmet title="MCP access" />
      <Result
        status="info"
        title={`${data.clientName} wants to use the Ydistri MCP`}
        subTitle={`It will act as ${asUser} and will be sent back to ${data.redirectHost}. This request expires at ${expires}.${
          decisionError ? ` ${decisionError}` : ''
        }`}
        extra={actions}
      />
    </>
  );
};

export default McpAuthorize;
